import { blankSection } from "../factories";
import { NotationContent } from "../model";
import { insertAt, mapSection, removeAt, swap, withSections } from "./internal";

/**
 * Section-level operations. All pure: `(content, args) => content`.
 *
 * BUG FIX — one counter was doing two unrelated jobs.
 *
 * The original class kept a single `sectionCount` that `deleteSection` never
 * decremented, and used it BOTH to name new sections AND to bounds-check the
 * move operations. Once it drifted above the real section count, a
 * `moveSectionDown` on the last section swapped it with an out-of-range slot
 * and produced an `undefined` section — which then got SAVED to Firestore.
 *
 * The two jobs are now separated:
 *   - bounds checks derive from `sections.length`, so they cannot drift;
 *   - naming uses `nextSectionNumber`, a monotonic counter that SHOULD never
 *     decrease (reusing numbers after a delete would produce duplicate names).
 */

export interface AddSectionArgs {
    position?: number;
    taalCount: number;
    vectorLength: number;
    /** Monotonic; only used to name the new section. */
    nextSectionNumber: number;
}

export function addSection<T extends NotationContent>(
    content: T,
    { position, taalCount, vectorLength, nextSectionNumber }: AddSectionArgs
): T {
    const section = blankSection(taalCount, vectorLength, nextSectionNumber);
    return withSections(content, insertAt(content.sections, section, position));
}

export function deleteSection<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number }
): T {
    return withSections(content, removeAt(content.sections, args.sectionIndex));
}

export function moveSectionUp<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number }
): T {
    const { sectionIndex } = args;
    // Bounds come from the actual array — no counter to drift out of sync.
    if (sectionIndex > 0 && sectionIndex < content.sections.length) {
        return withSections(
            content,
            swap(content.sections, sectionIndex, sectionIndex - 1)
        );
    }
    return content;
}

export function moveSectionDown<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number }
): T {
    const { sectionIndex } = args;
    if (sectionIndex >= 0 && sectionIndex < content.sections.length - 1) {
        return withSections(
            content,
            swap(content.sections, sectionIndex, sectionIndex + 1)
        );
    }
    return content;
}

export function setSectionName<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; name: string }
): T {
    return mapSection(content, args.sectionIndex, (section) => ({
        ...section,
        name: args.name,
    }));
}

export function getSectionName(
    content: NotationContent,
    sectionIndex: number
): string {
    return content.sections[sectionIndex].name;
}
